// src/lib/csv-versements.js — PARTS PAR VERSEMENT D'UNE RÉSERVATION IMPORTÉE
// (relecture équipe rouge de #1666, point A).
//
// Un séjour long est payé en plusieurs versements, chacun dans un relevé
// différent (constaté : lc2 83 nuits, l7 — deux mensualités du même montant).
// Le dernier relevé importé REMPLAÇAIT le montant : la 1re mensualité
// disparaissait. Chaque ligne importée porte maintenant ses parts, rangées
// par identifiant de versement :
//   Airbnb  : la référence « G-… » (colonne « Code de référence ») de la
//             ligne Payout qui précède les lignes Réservation (décision
//             Dimitri du 06/10 ; deux versements le même jour existent, la
//             date n'identifie pas — elle est gardée pour l'affichage) ;
//   Booking : « Payout ID », à défaut « Payout date ».
// RÈGLES :
//   - montant compté = somme des parts (versements différents) ;
//   - un versement déjà vu, réimporté, REMPLACE sa propre part (jamais
//     additionné une 2e fois) ;
//   - un remboursement négatif est un versement comme un autre : il se
//     soustrait ;
//   - lignes sans identifiant de versement : aucune part ; refusées face à
//     une réservation déjà détaillée, sinon comportement d'avant ;
//   - réservation existante SANS détail, montant ≠ 0, qui reçoit un
//     versement : refus (« avant le suivi des versements », décision 06/10).
// Fonctions PURES.

const _r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Ajoute `valeurs` à la part `id` (somme champ par champ ; null + null = null).
function ajouterPart(parts, id, valeurs) {
  const p = parts[id] || {};
  for (const k of Object.keys(valeurs)) {
    const v = valeurs[k];
    // La date du versement est une étiquette (affichée), pas un montant.
    if (k === "date") {
      if (v && !p.date) p.date = v;
      continue;
    }
    if (v == null || !Number.isFinite(Number(v))) {
      if (!(k in p)) p[k] = null;
      continue;
    }
    p[k] = _r2((p[k] == null ? 0 : Number(p[k])) + Number(v));
  }
  parts[id] = p;
  return parts;
}

// Parts existantes + nouvelles : un versement déjà vu est remplacé.
function fusionnerParts(existantes, nouvelles) {
  const base = existantes && typeof existantes === "object" ? existantes : {};
  return { ...base, ...(nouvelles || {}) };
}

// Somme d'un champ sur toutes les parts ; null si aucune part ne le porte.
function sommeParts(parts, champ) {
  let total = null;
  for (const id of Object.keys(parts || {})) {
    const v = parts[id] && parts[id][champ];
    if (v == null || !Number.isFinite(Number(v))) continue;
    total = (total == null ? 0 : total) + Number(v);
  }
  return total == null ? null : _r2(total);
}

// Somme des parts NÉGATIVES (remboursements) d'un champ ; 0 si aucune.
function sommeNegatives(parts, champ) {
  let total = 0;
  for (const id of Object.keys(parts || {})) {
    const v = Number(parts[id] && parts[id][champ]);
    if (Number.isFinite(v) && v < 0) total += v;
  }
  return _r2(total);
}

function partsValides(p) {
  return (
    !!p &&
    typeof p === "object" &&
    !Array.isArray(p) &&
    Object.keys(p).length > 0
  );
}

// Relecture tour 3 (point 2) : écart entre le montant d'une ligne détaillée et
// la somme de ses parts (correction faite à la main) — gardé sous cette clé.
const PART_CORRECTION = "correction_manuelle";

// Relecture tour 4 (point 2) — VERSEMENT DÉJÀ FACTURÉ DANS UN AUTRE MOIS.
// Avant d'enregistrer un versement sur une réservation sans détail, on
// cherche sur le même logement une ligne rangée dans un mois GELÉ (facture V
// ou P, ou facturé hors app) : période qui se chevauche (arrivée + nuits),
// même montant que le versement. Le nom ne fait que confirmer (décision du
// 06/10) : s'il est présent des deux côtés, le prénom doit être le même
// (comparé EN MÉMOIRE, jamais journalisé). Trouvée → { mk, numero }.
const _nomNormalise = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const _jourPlus = (iso, n) => {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + (Number(n) || 0));
  return d.toISOString().slice(0, 10);
};
const _fin = (ci, co, nuits) =>
  typeof co === "string" && co.length >= 10
    ? co.slice(0, 10)
    : _jourPlus(ci, Number(nuits) > 0 ? nuits : 1);

function dejaFactureAilleurs(data, lid, cible, moisGele) {
  if (!data || !lid || !cible || typeof moisGele !== "function") return null;
  const nom = _nomNormalise(cible.voyageur);
  const ci =
    typeof cible.checkIn === "string" ? cible.checkIn.slice(0, 10) : null;
  if (!ci || !Number.isFinite(Number(cible.montant))) return null;
  const co = _fin(ci, cible.checkOut, cible.nuits);
  let aTrancher = null;
  let chevauchement = null;
  const mks = Object.keys(data)
    .filter((mk) => /^\d{4}-\d{2}$/.test(mk))
    .sort();
  for (const mk of mks) {
    const resas = data[mk] && data[mk][lid] && data[mk][lid].reservations;
    if (!Array.isArray(resas)) continue;
    for (const r of resas) {
      if (!r || r.id === cible.exclureId) continue;
      // Décision 06/10 : le nom ne fait que CONFIRMER, s'il est présent des
      // deux côtés ; un prénom identique suffit.
      const nomR = _nomNormalise(r.voyageur);
      const prenomsDifferents = !!(
        nom &&
        nomR &&
        nomR.split(" ")[0] !== nom.split(" ")[0]
      );
      const montantR = Number(r.montant) || 0;
      // Les lignes à 0 € (ancien calendrier) sont ignorées (tour 6).
      if (!(montantR > 0)) continue;
      const memeMontant = Math.abs(montantR - Number(cible.montant)) <= 0.01;
      const rci =
        typeof (r.checkIn || r.date) === "string"
          ? (r.checkIn || r.date).slice(0, 10)
          : null;
      if (!rci) continue;
      const rco = _fin(rci, r.checkOut, r.nuits);
      if (!(rci < co && ci < rco)) continue;
      const gel = moisGele(lid, mk);
      if (!gel) continue;
      const numero =
        (gel.facture && gel.facture.invoice_number) || gel.numeros || null;
      // Tour 6 : dates qui CHEVAUCHENT, montant > 0 mais DIFFÉRENT → « à
      // trancher » (séjour à cheval sur un mois facturé), jamais libre.
      if (!memeMontant) {
        if (!chevauchement)
          chevauchement = { mk, numero, motif: "a_trancher_chevauchement" };
        continue;
      }
      // Tour 5 : même période et même montant mais prénoms différents → ce
      // n'est jamais « libre » : à trancher (on garde la recherche d'un
      // accord complet, qui l'emporte).
      if (prenomsDifferents) {
        if (!aTrancher)
          aTrancher = {
            mk,
            numero,
            prenomsDifferents: true,
            motif: "a_trancher_deja_facture",
          };
        continue;
      }
      return { mk, numero, motif: "deja_facture" };
    }
  }
  return aTrancher || chevauchement;
}

// Tour 5 (point 4, Booking) et tour 6 (Airbnb) — une part identifiée (Payout
// ID, référence G-) REMPLACE une part de secours (date seule, ou
// « date|versé ») du même jour et du même montant, au lieu de s'y ajouter ;
// et une part de secours qui arrive alors que la part identifiée
// correspondante existe est ignorée. → { existantes, nouvelles } (copies).
function dedoublonnerPartsDatees(existantes, nouvelles, champ) {
  const ex = { ...(existantes || {}) };
  const nv = { ...(nouvelles || {}) };
  const meme = (a, b) =>
    Math.abs((Number(a && a[champ]) || 0) - (Number(b && b[champ]) || 0)) <=
    0.01;
  // Part « de secours » : identifiée par sa date seule (Booking :
  // « AAAA-MM-JJ ») ou par « date|versé » (Airbnb sans référence G-, tour 6).
  const secours = (k, p) =>
    !!(p && p.date && (k === p.date || k.startsWith(p.date + "|")));
  for (const k of Object.keys(nv)) {
    const p = nv[k];
    if (!p || !p.date) continue;
    if (!secours(k, p)) {
      // Nouvelle part identifiée (Payout ID, G-) : retire UNE part de secours
      // du même jour et du même montant.
      const e = Object.keys(ex).find(
        (x) => secours(x, ex[x]) && ex[x].date === p.date && meme(ex[x], p),
      );
      if (e) delete ex[e];
    } else {
      // Nouvelle part de secours : la part identifiée correspondante existe
      // déjà → ignorée.
      const dejaId = Object.keys(ex).some(
        (x) =>
          !secours(x, ex[x]) &&
          ex[x] &&
          ex[x].date === p.date &&
          meme(ex[x], p),
      );
      if (dejaId) delete nv[k];
    }
  }
  return { existantes: ex, nouvelles: nv };
}

module.exports = {
  dedoublonnerPartsDatees,
  dejaFactureAilleurs,
  PART_CORRECTION,
  ajouterPart,
  fusionnerParts,
  sommeParts,
  sommeNegatives,
  partsValides,
};
